import { Button, Icon } from '@dixa/lib-shared-habla';
import * as Popover from '@radix-ui/react-popover';
import { ReactNode, useCallback, useState } from 'react';

import { ActivityLogDateRangePanel } from 'components/activityLog/components/filters/components/activityLogDateRangeFilter';
import { EventTypeList } from 'components/activityLog/components/filters/components/eventTypeFilter';
import { UserActivityLogFiltersValue } from 'components/activityLog/components/userActivityLogFeed';
import { userEventTypeLabels } from 'components/activityLog/components/userActivityTypes/filters/userEventTypeLabels';

import styles from './timelineFilters.module.scss';

type TimelineFilter = 'date-range' | 'event-types';

type TimelineFiltersProps = {
  readonly filters: UserActivityLogFiltersValue;
  readonly onChange: (filters: Partial<UserActivityLogFiltersValue>) => void;
};

type TimelineFilterItemProps = {
  readonly isOpen: boolean;
  readonly count: number;
  readonly label: string;
  readonly onOpenChange: (isOpen: boolean) => void;
  readonly children: ReactNode;
};

/** A menu entry that opens its filter to the left of the menu. */
const TimelineFilterItem = ({
  isOpen,
  count,
  label,
  onOpenChange,
  children,
}: TimelineFilterItemProps): JSX.Element => (
  <Popover.Root open={isOpen} onOpenChange={onOpenChange}>
    <Popover.Trigger asChild>
      <Button
        variant="tertiary"
        className={styles.menuItem}
        active={isOpen}
        label={count || undefined}
      >
        {label}
      </Button>
    </Popover.Trigger>

    <Popover.Portal>
      <Popover.Content
        className={styles.popoverContent}
        side="left"
        align="start"
        sideOffset={4}
      >
        {children}
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>
);

/**
 * The filter button in the timeline card header. It opens a menu with the date
 * range and event type filters, each opening to the left of the menu.
 */
export const TimelineFilters = ({
  filters,
  onChange,
}: TimelineFiltersProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<TimelineFilter>();

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setActiveFilter(undefined);
    }
  }, []);

  const handleFilterOpenChange = (filter: TimelineFilter) => (open: boolean) =>
    setActiveFilter(open ? filter : undefined);

  const eventTypes = filters.eventTypes ?? [];
  const dateRangeCount = filters.fromTs || filters.toTs ? 1 : 0;
  const activeFilterCount = dateRangeCount + (eventTypes.length ? 1 : 0);

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <Button
          variant="tertiary"
          active={isOpen}
          aria-label="Filters"
          label={activeFilterCount || undefined}
          // Keeps a click from also toggling the card.
          onClick={(event) => event.stopPropagation()}
        >
          <Icon name="filter" />
        </Button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className={styles.popoverContent}
          side="bottom"
          align="end"
          sideOffset={4}
          // Events bubble through the portal to the card header, which would toggle the card.
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.menu}>
            <TimelineFilterItem
              isOpen={activeFilter === 'date-range'}
              count={dateRangeCount}
              label="Date range"
              onOpenChange={handleFilterOpenChange('date-range')}
            >
              <ActivityLogDateRangePanel
                fromTs={filters.fromTs}
                toTs={filters.toTs}
                onChange={onChange}
              />
            </TimelineFilterItem>

            <TimelineFilterItem
              isOpen={activeFilter === 'event-types'}
              count={eventTypes.length}
              label="Event types"
              onOpenChange={handleFilterOpenChange('event-types')}
            >
              <div className={styles.eventTypes}>
                <EventTypeList
                  labels={userEventTypeLabels}
                  value={eventTypes}
                  onChange={(changed) => onChange({ eventTypes: changed })}
                />
              </div>
            </TimelineFilterItem>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
